import { createContext, FC, useEffect, useMemo, useState } from 'react';
import { find, keys, map, values } from 'lodash-es';
import { useSearchParams } from 'react-router';
import { Box, Flex, Spinner, Stack } from '@chakra-ui/react';

import { useDocumentForm } from '~/core/shared/api/documents';
import { useRequiredParams } from '~/core/shared/hooks';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import {
  DocumentForm,
  DocumentFormBlock,
  DocumentFormContextType,
} from '~/core/shared/types';
import { TenantDocumentRouteProps } from '../../../config';
import { MainPanel, SidebarPanel } from '../../crud';

export const DocumentFormContext = createContext<DocumentFormContextType>(
  {} as DocumentFormContextType,
);

type CrudTabProps = {
  formIdentifier: string;
  refreshOnSave?: boolean;
} & TenantDocumentRouteProps;

export const CrudTab: FC<CrudTabProps> = ({
  formIdentifier,
  refreshOnSave,
}: CrudTabProps) => {
  const [selectedFormBlock, setSelectedFormBlock] =
    useState<DocumentFormBlock>();
  const { uuid } = useRequiredParams(['uuid']);
  const [searchParams] = useSearchParams();
  /*
  TODO: @haxxxton, this request is weird. it doesnt appear to do the standard
  humps conversion of keys from snake to camel (at least not deeply)
  */
  const {
    documentForm,
    refetch: refetchForm,
    isLoading,
  } = useDocumentForm(uuid, { definition: 'doc' }, formIdentifier);
  const [form, setForm] = useState<DocumentForm>({} as DocumentForm);

  const blockMatchesId = (block: DocumentFormBlock, ids: string[]): boolean =>
    ids.includes(block.meta.id);

  const replaceBlocksInListById = useEventHandler(
    (
      list: DocumentFormBlock[],
      blocks: Record<string, DocumentFormBlock>,
    ): DocumentFormBlock[] => {
      const ids = keys(blocks);
      const found = !!find(list, (b) => blockMatchesId(b, ids));
      const updatedList = map(list, (b) => {
        if (found) {
          if (blockMatchesId(b, ids)) {
            return blocks[b.meta.id];
          }
        } else {
          return {
            ...b,
            blocks: replaceBlocksInListById(b.blocks, blocks),
          };
        }
        return b;
      });
      return updatedList;
    },
  );

  const getAllBlockIds = useEventHandler(
    (blocks: DocumentFormBlock[]): string[] => {
      let ids: string[] = [];

      blocks.forEach((block) => {
        ids.push(block.meta.id);
        if (block.blocks.length > 0) {
          ids = ids.concat(getAllBlockIds(block.blocks));
        }
      });

      return ids;
    },
  );

  const findBlockById = useEventHandler(
    (
      blocks: DocumentFormBlock[],
      id: string,
    ): DocumentFormBlock | undefined => {
      for (const block of blocks) {
        if (block.meta.id === id) return block;
        if (block.blocks && block.blocks.length > 0) {
          const found = findBlockById(block.blocks, id);
          if (found) return found;
        }
      }
      return undefined;
    },
  );

  const refreshSelectedBlock = useEventHandler(
    (updatedBlocks: Record<string, DocumentFormBlock>): void => {
      if (!selectedFormBlock) return;
      const updatedIds = getAllBlockIds(values(updatedBlocks));
      if (updatedIds.includes(selectedFormBlock.meta.id)) {
        setSelectedFormBlock(updatedBlocks[selectedFormBlock.meta.id]);
      } else if (
        documentForm?.content &&
        !findBlockById(documentForm.content, selectedFormBlock.meta.id)
      ) {
        // @TODO: set undefined if we just deleted the selected block entity
        setSelectedFormBlock(undefined);
      }
    },
  );

  const updateFormBlocksById = useEventHandler(
    (blocks: Record<string, DocumentFormBlock>): void => {
      const updatedForm = {
        ...form,
        content: replaceBlocksInListById(form.content, blocks),
      };

      refreshSelectedBlock(blocks);
      setForm(updatedForm);
    },
  );

  const findBlockByBlockPathAndTitle = useEventHandler(
    (
      blocks: DocumentFormBlock[],
      blockPath: string,
      title: string,
    ): DocumentFormBlock | undefined => {
      for (const block of blocks) {
        if (block.meta.blockPath === blockPath && block.meta.title === title)
          return block;
        if (block.blocks && block.blocks.length > 0) {
          const found = findBlockByBlockPathAndTitle(
            block.blocks,
            blockPath,
            title,
          );
          if (found) return found;
        }
      }
      return undefined;
    },
  );

  useEffect(() => {
    if (!documentForm) return;
    setForm(documentForm);

    if (selectedFormBlock) {
      const reselected = findBlockByBlockPathAndTitle(
        documentForm.content,
        selectedFormBlock.meta.blockPath,
        selectedFormBlock.meta.title,
      );
      if (reselected) setSelectedFormBlock(reselected);
      return;
    }

    const linkParam = searchParams.get('link');

    const blockToSelect = linkParam
      ? findBlockById(documentForm.content, linkParam)
      : documentForm.content?.[0];

    if (documentForm.content.length > 0) {
      setSelectedFormBlock(blockToSelect);
    }
  }, [documentForm, searchParams]);

  const refreshFormContent = useEventHandler(
    (blocks: Record<string, DocumentFormBlock>) => {
      const updatedForm = {
        ...form,
        content: values(blocks),
      };

      refreshSelectedBlock(blocks);
      setForm(updatedForm);
    },
  );

  const setSelectedBlock = useEventHandler((block: DocumentFormBlock) => {
    setSelectedFormBlock(block);
  });

  const updateFormBlockById = useEventHandler(
    (id: string, block: DocumentFormBlock) =>
      updateFormBlocksById({
        [id]: block,
      }),
  );

  const documentFormContextValue = useMemo(
    () => ({
      form,
      formIdentifier,
      refreshFormContent,
      selectedBlock: selectedFormBlock,
      setSelectedBlock,
      updateFormBlockById,
      updateFormBlocksById,
      refetchForm,
      refreshOnSave,
    }),
    [
      form,
      formIdentifier,
      refreshFormContent,
      selectedFormBlock,
      setSelectedBlock,
      updateFormBlockById,
      updateFormBlocksById,
      refetchForm,
      refreshOnSave,
    ],
  );

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <DocumentFormContext.Provider value={documentFormContextValue}>
      <Stack direction="row" gap={0}>
        <Flex bg="#f9f8f8" direction="column" maxWidth="20%" minWidth="350px">
          <SidebarPanel />
        </Flex>
        <Box
          border="solid 1px"
          borderColor="gray.200"
          height="100vh"
          width="100%"
        >
          {selectedFormBlock && <MainPanel block={selectedFormBlock} />}
        </Box>
      </Stack>
    </DocumentFormContext.Provider>
  );
};
